import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { tokens } from "../../../theme";
import Header from "../../../global_components/parameterized/Header";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";

import MANIFEST from "../drillDashboard/drillOverview.json";

// ─── Constants ────────────────────────────────────────────────────────────────
const SENTINEL = 65535;
const DATA_DIR = "/rwth/drill_data";

// GitLab repository for this dataset
const GITLAB_URL =
  "https://git.rwth-aachen.de/nfdi4ing/education/exampledatasets/drillprocess-timeseries-machinelearning/";

const SHIFT_LABELS = {
  Shift1: "Shift 1",
  Shift2: "Shift 2",
  Shift3: "Shift 3",
};

// ─── Failure styling ──────────────────────────────────────────────────────────
const FAILURE_ZONE_COLORS = {
  Tool: "rgba(55, 138, 221, 0.18)", // blue  – tool wear
  Temp: "rgba(186, 117, 23, 0.18)", // amber – overheating
};

const FAILURE_LABEL_COLORS = {
  Tool: "#185FA5",
  Temp: "#854F0B",
};

const FAILURE_DISPLAY = {
  Tool: "Tool wear",
  Temp: "Overheating",
};

const getStatusStyle = (failure) => {
  if (failure === "Tool") return { bg: "#EAF1F8", color: "#40576D" };
  if (failure === "Temp") return { bg: "#F3EEE8", color: "#6C5A45" };
  return { bg: "#F2F4F7", color: "#667085" };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isSentinel = (v) => v === SENTINEL;
const isNullVal = (v) => v === null || v === undefined;

const fmtNumber = (v) => {
  if (isNullVal(v)) return "—";
  if (isSentinel(v)) return "OVF";
  return parseFloat(v).toFixed(2);
};

const toISODate = (ddmmyyyy) => {
  const [d, m, y] = ddmmyyyy.split(".");
  return `${y}-${m}-${d}`;
};

const toDisplayDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const getMachine = (filename) => {
  const match = filename.match(/_(\d{2})\.csv$/);
  return match ? String(parseInt(match[1], 10)) : "?";
};

const toJsonFilename = (csvName) => csvName.replace(/\.csv$/, ".json");

// ─── Enrich manifest once ─────────────────────────────────────────────────────
const ENRICHED = MANIFEST.map((e) => ({
  ...e,
  isoDate: toISODate(e.date),
  machine: getMachine(e.filename),
  jsonFile: toJsonFilename(e.filename),
}));

// ─── Metric definitions ───────────────────────────────────────────────────────
const METRICS = [
  { key: "speed", label: "Speed", unit: "rpm", color: "#378ADD" },
  { key: "torque", label: "Torque", unit: "Nm", color: "#D85A30" },
  { key: "air", label: "Air Temp", unit: "°C", color: "#1D9E75" },
  { key: "proc", label: "Process Temp", unit: "°C", color: "#BA7517" },
];

// ─── Custom DataGrid toolbar with GitLab link ─────────────────────────────────
const CustomToolbar = ({ gitlabUrl }) => (
  <GridToolbarContainer sx={{ p: "8px 8px 4px 8px" }}>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <Button
      component="a"
      href={gitlabUrl}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
      size="small"
      variant="contained"
      disableElevation
      sx={{
        backgroundColor: "#11192A",
        color: "#fff",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.78rem",
        borderRadius: "4px",
        px: 1.25,
        py: 0.5,
        "&:hover": { backgroundColor: "#1f2a45" },
      }}
    >
      View dataset on GitLab
    </Button>
  </GridToolbarContainer>
);

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  const failure = payload[0]?.payload?.failure;
  return (
    <Box
      sx={{
        background: "rgba(20,20,20,0.88)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "6px",
        padding: "6px 10px",
        fontSize: "11px",
        color: "#e0e0e0",
        lineHeight: 1.6,
      }}
    >
      <div style={{ fontWeight: 600, color: "#fff", marginBottom: 2 }}>
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.value !== null && p.value !== undefined ? p.value.toFixed(2) : "—"}{" "}
          {unit}
        </div>
      ))}
      {failure && failure !== "OK" && (
        <div
          style={{
            marginTop: 2,
            color: FAILURE_LABEL_COLORS[failure],
            fontWeight: 500,
          }}
        >
          ⚠ {FAILURE_DISPLAY[failure]}
        </div>
      )}
    </Box>
  );
};

// ─── Mini chart snippet ───────────────────────────────────────────────────────
const MiniChart = ({
  label,
  unit,
  color,
  dataKey,
  chartData,
  failureZones,
  loading,
  colors,
  axisColor,
  gridColor,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      backgroundColor: colors.primary[400],
      borderRadius: "8px",
      p: "8px 8px 4px 8px",
    }}
  >
    {/* Header */}
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb="3px"
    >
      <Typography
        sx={{ fontSize: "11px", fontWeight: 600, color: colors.grey[100] }}
      >
        {label}
        <Typography
          component="span"
          sx={{
            fontSize: "10px",
            fontWeight: 400,
            color: colors.grey[400],
            ml: "4px",
          }}
        >
          ({unit})
        </Typography>
      </Typography>
      <Box display="flex" gap="6px" alignItems="center">
        {Object.entries(FAILURE_DISPLAY).map(([k, l]) => (
          <Box key={k} display="flex" alignItems="center" gap="2px">
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "1px",
                backgroundColor: FAILURE_ZONE_COLORS[k].replace("0.18", "0.6"),
                border: `1px solid ${FAILURE_LABEL_COLORS[k]}`,
              }}
            />
            <Typography
              sx={{ fontSize: "9px", color: FAILURE_LABEL_COLORS[k] }}
            >
              {l}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* Chart body */}
    {loading ? (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="200px"
      >
        <CircularProgress size={14} sx={{ color: colors.greenAccent[400] }} />
      </Box>
    ) : chartData.length === 0 ? (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="200px"
      >
        <Typography sx={{ color: colors.grey[500], fontSize: "10px" }}>
          No data
        </Typography>
      </Box>
    ) : (
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart
          data={chartData}
          margin={{ top: 2, right: 4, left: 0, bottom: 12 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="time"
            type="category"
            tick={{ fontSize: 8, fill: axisColor }}
            interval="preserveStartEnd"
            angle={-30}
            textAnchor="end"
            height={24}
          />
          <YAxis
            width={32}
            tick={{ fontSize: 8, fill: color }}
            tickFormatter={(v) => `${Math.round(v)}`}
          />
          <Tooltip content={<ChartTooltip unit={unit} />} />
          {failureZones.map((zone, i) => (
            <ReferenceArea
              key={i}
              x1={zone.x1}
              x2={zone.x2}
              fill={FAILURE_ZONE_COLORS[zone.type]}
              stroke={FAILURE_LABEL_COLORS[zone.type]}
              strokeOpacity={0.5}
              strokeWidth={1}
            />
          ))}
          <Line
            type="monotone"
            dataKey={dataKey}
            name={label}
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    )}
  </Box>
);

// ─── Build contiguous failure zones ──────────────────────────────────────────
// Keys on the `time` string so ReferenceArea x1/x2 match the categorical XAxis.
const buildFailureZones = (chartData) => {
  const zones = [];
  let current = null;

  chartData.forEach((row) => {
    const f = row.failure;
    if (f && f !== "OK" && FAILURE_ZONE_COLORS[f]) {
      if (!current || current.type !== f) {
        if (current) zones.push(current);
        current = { type: f, x1: row.time, x2: row.time };
      } else {
        current.x2 = row.time;
      }
    } else {
      if (current) {
        zones.push(current);
        current = null;
      }
    }
  });
  if (current) zones.push(current);
  return zones;
};

// ─── Module-level fetch cache (survives re-renders, cleared on page reload) ───
const FILE_CACHE = new Map();

const fetchWithCache = (jsonFile) => {
  if (FILE_CACHE.has(jsonFile))
    return Promise.resolve(FILE_CACHE.get(jsonFile));
  return fetch(`${DATA_DIR}/${jsonFile}`)
    .then((res) => {
      if (!res.ok)
        throw new Error(`Could not load ${jsonFile} (${res.status})`);
      return res.json();
    })
    .then((data) => {
      FILE_CACHE.set(jsonFile, data);
      return data;
    });
};

// ─── Component ────────────────────────────────────────────────────────────────
const DrillExperiments = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [selDate, setSelDate] = useState("");
  const [selMachine, setSelMachine] = useState("");
  const [selShift, setSelShift] = useState("");
  const [selStatus, setSelStatus] = useState("all");

  // ── Data state ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Available filter options ───────────────────────────────────────────────
  const availableDates = useMemo(
    () => [...new Set(ENRICHED.map((e) => e.isoDate))].sort(),
    [],
  );

  useEffect(() => {
    if (availableDates.length > 0 && selDate === "") {
      const firstDate = availableDates[0];
      const machinesForDate = [
        ...new Set(
          ENRICHED.filter((e) => e.isoDate === firstDate).map((e) => e.machine),
        ),
      ].sort();
      const shiftsForDate = [
        ...new Set(
          ENRICHED.filter((e) => e.isoDate === firstDate).map((e) => e.shift),
        ),
      ].sort();
      setSelDate(firstDate);
      setSelMachine(machinesForDate[0] || "");
      setSelShift(shiftsForDate[0] || "");
    }
  }, [availableDates, selDate]);

  const availableMachines = useMemo(() => {
    const subset = ENRICHED.filter((e) => !selDate || e.isoDate === selDate);
    return [...new Set(subset.map((e) => e.machine))].sort();
  }, [selDate]);

  const availableShifts = useMemo(() => {
    const subset = ENRICHED.filter((e) => !selDate || e.isoDate === selDate);
    return [...new Set(subset.map((e) => e.shift))].sort();
  }, [selDate]);

  // ── Matching entries ───────────────────────────────────────────────────────
  const matchingEntries = useMemo(
    () =>
      ENRICHED.filter((e) => {
        if (selDate && e.isoDate !== selDate) return false;
        if (selMachine && e.machine !== selMachine) return false;
        if (selShift && e.shift !== selShift) return false;
        return true;
      }),
    [selDate, selMachine, selShift],
  );

  // ── Background preload: warm the cache for all files on mount ────────────────
  useEffect(() => {
    ENRICHED.forEach((entry) => fetchWithCache(entry.jsonFile).catch(() => {}));
  }, []);

  // ── Fetch JSON files (cache-first, debounced 80ms) ────────────────────────
  useEffect(() => {
    if (matchingEntries.length === 0) {
      setRows([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      // If all files already cached, skip loading spinner entirely
      const allCached = matchingEntries.every((e) =>
        FILE_CACHE.has(e.jsonFile),
      );
      if (!allCached) setLoading(true);
      setError(null);

      Promise.all(
        matchingEntries.map((entry) =>
          fetchWithCache(entry.jsonFile).then((data) =>
            data.map((row, idx) => ({
              ...row,
              id: `${entry.jsonFile}-${idx}`,
              machine: `Machine ${entry.machine}`,
              shift: SHIFT_LABELS[entry.shift] ?? entry.shift,
              date: entry.date,
            })),
          ),
        ),
      )
        .then((results) => {
          if (!cancelled) {
            setRows(results.flat());
            setLoading(false);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err.message);
            setLoading(false);
          }
        });
    }, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [matchingEntries]);

  // ── Client-side status filter ──────────────────────────────────────────────
  const filteredRows = useMemo(
    () =>
      selStatus === "all" ? rows : rows.filter((r) => r.failure === selStatus),
    [rows, selStatus],
  );

  // ── Chart data: sorted by time, all 4 metrics ─────────────────────────────
  const chartData = useMemo(() => {
    const sorted = [...rows].sort((a, b) =>
      a.time < b.time ? -1 : a.time > b.time ? 1 : 0,
    );
    return sorted.map((row) => ({
      time: row.time,
      speed:
        isSentinel(row.speed) || isNullVal(row.speed)
          ? null
          : parseFloat(row.speed),
      torque:
        isSentinel(row.torque) || isNullVal(row.torque)
          ? null
          : parseFloat(row.torque),
      air:
        isSentinel(row.air) || isNullVal(row.air) ? null : parseFloat(row.air),
      proc:
        isSentinel(row.proc) || isNullVal(row.proc)
          ? null
          : parseFloat(row.proc),
      failure: row.failure ?? "OK",
    }));
  }, [rows]);

  const failureZones = useMemo(() => buildFailureZones(chartData), [chartData]);

  // ── Reset cascading filters on date change ─────────────────────────────────
  const handleDateChange = (newDate) => {
    setSelDate(newDate);
    const machinesForDate = [
      ...new Set(
        ENRICHED.filter((e) => e.isoDate === newDate).map((e) => e.machine),
      ),
    ].sort();
    const shiftsForDate = [
      ...new Set(
        ENRICHED.filter((e) => e.isoDate === newDate).map((e) => e.shift),
      ),
    ].sort();
    setSelMachine(machinesForDate[0] || "");
    setSelShift(shiftsForDate[0] || "");
    setSelStatus("all");
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "time", headerName: "Time", flex: 1 },
    { field: "machine", headerName: "Machine", flex: 1 },
    { field: "shift", headerName: "Shift", flex: 0.8 },
    {
      field: "speed",
      headerName: "Speed (rpm)",
      flex: 1.2,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) => fmtNumber(params.value),
      cellClassName: (params) =>
        isSentinel(params.value)
          ? "cell--overflow"
          : isNullVal(params.value)
            ? "cell--null"
            : "",
    },
    {
      field: "torque",
      headerName: "Torque (Nm)",
      flex: 1.2,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) => fmtNumber(params.value),
      cellClassName: (params) =>
        isSentinel(params.value)
          ? "cell--overflow"
          : isNullVal(params.value)
            ? "cell--null"
            : "",
    },
    {
      field: "air",
      headerName: "Air Temp (°C)",
      flex: 1.2,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) => fmtNumber(params.value),
      cellClassName: (params) =>
        isSentinel(params.value)
          ? "cell--overflow"
          : isNullVal(params.value)
            ? "cell--null"
            : "",
    },
    {
      field: "proc",
      headerName: "Process Temp (°C)",
      flex: 1.5,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) => fmtNumber(params.value),
      cellClassName: (params) =>
        isSentinel(params.value)
          ? "cell--overflow"
          : isNullVal(params.value)
            ? "cell--null"
            : "",
    },
    {
      field: "failure",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => {
        const style = getStatusStyle(params.value);
        return (
          <Box
            sx={{
              backgroundColor: style.bg,
              color: style.color,
              borderRadius: "4px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {params.value
              ? (FAILURE_DISPLAY[params.value] ?? params.value)
              : "OK"}
          </Box>
        );
      },
    },
  ];

  // ── Select styles ──────────────────────────────────────────────────────────
  const selectSx = {
    minWidth: 150,
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[400],
      "& fieldset": { borderColor: colors.grey[600] },
      "&:hover fieldset": { borderColor: colors.grey[400] },
      "&.Mui-focused fieldset": { borderColor: colors.greenAccent[400] },
    },
    "& .MuiInputLabel-root": {
      color: colors.grey[300],
      "&.MuiInputLabel-shrink": { color: colors.grey[200] },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: colors.greenAccent[400] },
    "& .MuiSelect-select": { color: colors.grey[100] },
    "& .MuiSvgIcon-root": { color: colors.grey[400] },
  };

  // ─── Recharts theme colors ────────────────────────────────────────────────
  const isDark = theme.palette.mode === "dark";
  const axisColor = isDark ? "#9e9e9e" : "#666";
  const gridColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";

  const [showCharts, setShowCharts] = useState(false);
  return (
    <Box m="20px">
      {/* ── Header ── */}
      <Header
        title="DRILL EXPERIMENTS"
        subtitle="Select an experiment date, machine, and shift to load the corresponding data:"
      />

      {/* ── Filters row ── */}
      <Box
        display="flex"
        flexDirection="row"
        gap="12px"
        alignItems="center"
        mt="20px"
        mb="12px"
        flexWrap="wrap"
      >
        <FormControl size="small" sx={selectSx}>
          <InputLabel shrink>Date</InputLabel>
          <Select
            value={selDate}
            label="Date"
            notched
            onChange={(e) => handleDateChange(e.target.value)}
          >
            {availableDates.map((iso) => (
              <MenuItem key={iso} value={iso}>
                {toDisplayDate(iso)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={selectSx}>
          <InputLabel shrink>Machine</InputLabel>
          <Select
            value={selMachine}
            label="Machine"
            notched
            onChange={(e) => setSelMachine(e.target.value)}
          >
            {availableMachines.map((m) => (
              <MenuItem key={m} value={m}>
                Machine {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={selectSx}>
          <InputLabel shrink>Shift</InputLabel>
          <Select
            value={selShift}
            label="Shift"
            notched
            onChange={(e) => setSelShift(e.target.value)}
          >
            {availableShifts.map((s) => (
              <MenuItem key={s} value={s}>
                {SHIFT_LABELS[s] ?? s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={selectSx}>
          <InputLabel shrink>Status</InputLabel>
          <Select
            value={selStatus}
            label="Status"
            notched
            onChange={(e) => setSelStatus(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="OK">OK</MenuItem>
            <MenuItem value="Tool">Tool wear</MenuItem>
            <MenuItem value="Temp">Overheating</MenuItem>
          </Select>
        </FormControl>

        {/* Show/Hide graphs toggle */}
        <Button
          variant="contained"
          startIcon={<BarChartOutlinedIcon />}
          onClick={() => setShowCharts((v) => !v)}
          disableElevation
          sx={{
            backgroundColor: "#11192A",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "0.9rem",
            borderRadius: "8px",
            textTransform: "none",
            px: 2,
          }}
        >
          {showCharts ? "Hide graphs" : "Show graphs"}
        </Button>
      </Box>

      {/* ── 1×4 chart strip — full width, only rendered when visible ── */}
      {showCharts && (
        <Box display="flex" gap="8px" mb="20px">
          {METRICS.map((m) => (
            <MiniChart
              key={m.key}
              label={m.label}
              unit={m.unit}
              color={m.color}
              dataKey={m.key}
              chartData={chartData}
              failureZones={failureZones}
              loading={loading}
              colors={colors}
              axisColor={axisColor}
              gridColor={gridColor}
            />
          ))}
        </Box>
      )}

      {/* ── Error banner ── */}
      {error && (
        <Box
          mb="16px"
          p="12px 16px"
          sx={{
            backgroundColor: colors.redAccent[900],
            borderRadius: "4px",
            borderLeft: `4px solid ${colors.redAccent[400]}`,
          }}
        >
          <Typography sx={{ color: colors.redAccent[200], fontSize: "13px" }}>
            ⚠ {error} — make sure files are in{" "}
            <code>public/rwth/drill_data/</code> with .json extension.
          </Typography>
        </Box>
      )}

      {/* ── DataGrid ── */}
      <Box
        m="0 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[400],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[400],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .cell--overflow": {
            color: colors.redAccent[300],
            fontWeight: 600,
          },
          "& .cell--null": { color: colors.grey[500], fontStyle: "italic" },
        }}
      >
        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CircularProgress sx={{ color: colors.greenAccent[400] }} />
            <Typography ml="16px" sx={{ color: colors.grey[300] }}>
              Loading experiment data…
            </Typography>
          </Box>
        ) : (
          <DataGrid
            checkboxSelection
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.id}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ toolbar: { gitlabUrl: GITLAB_URL } }}
            initialState={{
              sorting: { sortModel: [{ field: "time", sort: "asc" }] },
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            pageSizeOptions={[100, 500, 1000]}
          />
        )}
      </Box>
    </Box>
  );
};

export default DrillExperiments;
